import { useEffect, useRef, useState } from "react";
import { Crosshair, Play, RotateCcw, Zap } from "lucide-react";
import AdReward from "./_adReward";

/**
 * Jixxeo Blaster — a canvas twin-stick shooter. WASD/arrow keys to move,
 * mouse to aim, click to fire. Waves of neon drones spawn. Purely
 * client-side, no dependencies beyond canvas.
 */

const W = 900;
const H = 560;

export default function JixxeoBlaster() {
  const canvasRef = useRef(null);
  const stateRef = useRef(null);
  const [running, setRunning] = useState(false);
  const [gameOver, setGameOver] = useState(false);
  const [score, setScore] = useState(0);
  const [wave, setWave] = useState(1);
  const [hp, setHp] = useState(3);
  const [best, setBest] = useState(() => Number(localStorage.getItem("jixxeo_blaster_best") || 0));

  // Persist best score
  useEffect(() => {
    if (score > best) {
      setBest(score);
      localStorage.setItem("jixxeo_blaster_best", String(score));
    }
  }, [score, best]);

  const reset = () => {
    setScore(0); setWave(1); setHp(3); setGameOver(false); setRunning(true);
    const S = {
      player: { x: W / 2, y: H / 2, r: 12, vx: 0, vy: 0, speed: 3.6 },
      bullets: [], enemies: [], particles: [],
      keys: {}, mouse: { x: W / 2, y: 60, down: false }, cooldown: 0,
      spawnTimer: 60, waveCount: 0, waveLeft: 6, kills: 0,
    };
    stateRef.current = S;
  };

  const start = () => {
    if (running) return;
    reset();
  };

  // Input
  useEffect(() => {
    const kd = (e) => { const s = stateRef.current; if (!s) return; s.keys[e.key.toLowerCase()] = true; };
    const ku = (e) => { const s = stateRef.current; if (!s) return; s.keys[e.key.toLowerCase()] = false; };
    window.addEventListener("keydown", kd);
    window.addEventListener("keyup", ku);
    return () => { window.removeEventListener("keydown", kd); window.removeEventListener("keyup", ku); };
  }, []);

  const mouseMove = (e) => {
    const s = stateRef.current; if (!s) return;
    const rect = canvasRef.current.getBoundingClientRect();
    s.mouse.x = ((e.clientX - rect.left) / rect.width) * W;
    s.mouse.y = ((e.clientY - rect.top) / rect.height) * H;
  };
  const mouseDown = () => { const s = stateRef.current; if (s) s.mouse.down = true; };
  const mouseUp = () => { const s = stateRef.current; if (s) s.mouse.down = false; };

  // Loop
  useEffect(() => {
    if (!running) return;
    const ctx = canvasRef.current.getContext("2d");
    let raf;
    const spawnEnemy = (S) => {
      // spawn just outside canvas
      const side = Math.floor(Math.random() * 4);
      let x = 0, y = 0;
      if (side === 0) { x = Math.random() * W; y = -20; }
      else if (side === 1) { x = W + 20; y = Math.random() * H; }
      else if (side === 2) { x = Math.random() * W; y = H + 20; }
      else { x = -20; y = Math.random() * H; }
      const speed = 0.9 + Math.random() * 0.4 + S.waveCount * 0.15;
      const hp = 1 + Math.floor(S.waveCount / 3);
      S.enemies.push({ x, y, r: 12 + Math.random() * 6, speed, hp, hue: [0, 60, 180, 300][Math.floor(Math.random() * 4)] });
    };
    const step = () => {
      const S = stateRef.current; if (!S) return;
      // input
      let dx = 0, dy = 0;
      if (S.keys["w"] || S.keys["arrowup"]) dy -= 1;
      if (S.keys["s"] || S.keys["arrowdown"]) dy += 1;
      if (S.keys["a"] || S.keys["arrowleft"]) dx -= 1;
      if (S.keys["d"] || S.keys["arrowright"]) dx += 1;
      const mag = Math.hypot(dx, dy) || 1;
      S.player.x = Math.max(20, Math.min(W - 20, S.player.x + (dx / mag) * S.player.speed));
      S.player.y = Math.max(20, Math.min(H - 20, S.player.y + (dy / mag) * S.player.speed));
      // firing
      S.cooldown = Math.max(0, S.cooldown - 1);
      if ((S.mouse.down || S.keys[" "]) && S.cooldown === 0) {
        const a = Math.atan2(S.mouse.y - S.player.y, S.mouse.x - S.player.x);
        S.bullets.push({ x: S.player.x, y: S.player.y, vx: Math.cos(a) * 8, vy: Math.sin(a) * 8, life: 60 });
        S.cooldown = 7;
      }
      // bullets
      S.bullets = S.bullets.filter((b) => { b.x += b.vx; b.y += b.vy; b.life--; return b.life > 0 && b.x > -20 && b.x < W + 20 && b.y > -20 && b.y < H + 20; });
      // enemies
      S.enemies.forEach((e) => {
        const a = Math.atan2(S.player.y - e.y, S.player.x - e.x);
        e.x += Math.cos(a) * e.speed;
        e.y += Math.sin(a) * e.speed;
      });
      // collisions bullet -> enemy
      for (let i = S.enemies.length - 1; i >= 0; i--) {
        const en = S.enemies[i];
        for (let j = S.bullets.length - 1; j >= 0; j--) {
          const b = S.bullets[j];
          const d = Math.hypot(b.x - en.x, b.y - en.y);
          if (d < en.r) {
            S.bullets.splice(j, 1);
            en.hp -= 1;
            if (en.hp <= 0) {
              // particles
              for (let k = 0; k < 12; k++) {
                S.particles.push({ x: en.x, y: en.y, vx: (Math.random() - 0.5) * 6, vy: (Math.random() - 0.5) * 6, life: 22, hue: en.hue });
              }
              S.enemies.splice(i, 1);
              S.kills += 1;
              setScore((s) => s + 10 + S.waveCount);
              S.waveLeft -= 1;
            }
            break;
          }
        }
      }
      // player collision
      for (let i = S.enemies.length - 1; i >= 0; i--) {
        const en = S.enemies[i];
        if (Math.hypot(en.x - S.player.x, en.y - S.player.y) < en.r + S.player.r) {
          S.enemies.splice(i, 1);
          setHp((h) => {
            const nh = h - 1;
            if (nh <= 0) { setRunning(false); setGameOver(true); }
            return nh;
          });
        }
      }
      // wave management
      if (S.waveLeft <= 0 && S.enemies.length === 0) {
        S.waveCount += 1;
        setWave(S.waveCount + 1);
        S.waveLeft = 6 + S.waveCount * 3;
        S.spawnTimer = 30;
      }
      S.spawnTimer -= 1;
      if (S.spawnTimer <= 0 && S.waveLeft > S.enemies.length) {
        spawnEnemy(S);
        S.spawnTimer = Math.max(18, 45 - S.waveCount * 2);
      }
      // particles
      S.particles = S.particles.filter((p) => { p.x += p.vx; p.y += p.vy; p.vx *= 0.9; p.vy *= 0.9; p.life -= 1; return p.life > 0; });

      // ---- render ----
      ctx.fillStyle = "#050505";
      ctx.fillRect(0, 0, W, H);
      // grid
      ctx.strokeStyle = "rgba(0,240,255,0.08)"; ctx.lineWidth = 1;
      for (let x = 0; x < W; x += 40) { ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, H); ctx.stroke(); }
      for (let y = 0; y < H; y += 40) { ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(W, y); ctx.stroke(); }
      // particles
      S.particles.forEach((p) => {
        ctx.fillStyle = `hsl(${p.hue}, 90%, 55%)`;
        ctx.globalAlpha = Math.max(0, p.life / 22);
        ctx.fillRect(p.x - 2, p.y - 2, 4, 4);
      });
      ctx.globalAlpha = 1;
      // bullets
      S.bullets.forEach((b) => {
        ctx.fillStyle = "#FFE300"; ctx.shadowColor = "#FFE300"; ctx.shadowBlur = 12;
        ctx.beginPath(); ctx.arc(b.x, b.y, 3, 0, Math.PI * 2); ctx.fill();
      });
      ctx.shadowBlur = 0;
      // enemies
      S.enemies.forEach((e) => {
        ctx.fillStyle = `hsl(${e.hue}, 90%, 55%)`;
        ctx.shadowColor = `hsl(${e.hue}, 90%, 55%)`; ctx.shadowBlur = 16;
        ctx.beginPath(); ctx.arc(e.x, e.y, e.r, 0, Math.PI * 2); ctx.fill();
      });
      ctx.shadowBlur = 0;
      // player
      const a = Math.atan2(S.mouse.y - S.player.y, S.mouse.x - S.player.x);
      ctx.save(); ctx.translate(S.player.x, S.player.y); ctx.rotate(a);
      ctx.fillStyle = "#00F0FF"; ctx.shadowColor = "#00F0FF"; ctx.shadowBlur = 20;
      ctx.beginPath(); ctx.moveTo(15, 0); ctx.lineTo(-10, -10); ctx.lineTo(-6, 0); ctx.lineTo(-10, 10); ctx.closePath(); ctx.fill();
      ctx.restore(); ctx.shadowBlur = 0;

      raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [running]);

  return (
    <div className="min-h-screen bg-[#050505] text-white p-4 md:p-8 relative overflow-hidden flex flex-col">
      <div className="absolute inset-0 pointer-events-none opacity-25" style={{
        background: "radial-gradient(ellipse at 20% 20%, rgba(255,0,60,0.20), transparent 60%), radial-gradient(ellipse at 80% 80%, rgba(0,240,255,0.15), transparent 60%)",
      }} />
      <div className="relative max-w-5xl w-full mx-auto flex flex-col">
        <div className="flex items-center gap-3 mb-4">
          <span className="w-10 h-10 rounded-xl bg-[#FF003C] flex items-center justify-center shadow-[0_0_24px_rgba(255,0,60,0.5)]">
            <Crosshair className="w-5 h-5 text-black" strokeWidth={3} />
          </span>
          <div>
            <div className="mono text-[9px] uppercase tracking-[0.25em] text-[#00F0FF]">// jixxeo apps · demo pwa</div>
            <h1 className="font-heading font-black text-2xl md:text-3xl uppercase tracking-tighter leading-none">Jixxeo Blaster</h1>
          </div>
          <div className="ml-auto flex items-center gap-2">
            <Chip label="Wave" value={wave} color="#00F0FF" />
            <Chip label="Score" value={score} color="#FFE300" />
            <Chip label="HP" value={"♥".repeat(Math.max(0, hp))} color="#FF003C" />
            <Chip label="Best" value={best} color="#39FF14" />
          </div>
        </div>

        <div className="relative rounded-2xl overflow-hidden border border-white/10 bg-black" style={{ aspectRatio: `${W} / ${H}` }}>
          <canvas
            ref={canvasRef}
            width={W} height={H}
            className="w-full h-full cursor-crosshair touch-none"
            onMouseMove={mouseMove} onMouseDown={mouseDown} onMouseUp={mouseUp} onMouseLeave={mouseUp}
          />
          {!running && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/70 backdrop-blur-sm">
              <div className="text-center max-w-md px-4">
                <div className="mono text-[10px] uppercase tracking-[0.3em] text-[#00F0FF] mb-2">// mission briefing</div>
                <div className="font-heading font-black text-4xl uppercase tracking-tighter mb-2">
                  {gameOver ? "You died" : "Survive the swarm"}
                </div>
                {gameOver && (
                  <div className="mono text-sm text-white/70 mb-3">Final score <span className="text-[#FFE300] font-bold">{score}</span> · Wave <span className="text-[#00F0FF] font-bold">{wave}</span></div>
                )}
                {gameOver && (
                  <div className="mb-4 flex justify-center"><AdReward game="jixxeo-blaster" compact /></div>
                )}
                <div className="mono text-[10px] uppercase tracking-widest text-white/50 mb-6">
                  WASD move · mouse aim · click / space fire
                </div>
                <button data-testid="embed-blaster-start" onClick={start}
                  className="inline-flex items-center gap-2 bg-[#FF003C] hover:bg-white text-black font-black mono text-xs uppercase tracking-widest px-8 py-3 rounded-full pulse-neon">
                  {gameOver ? <RotateCcw className="w-4 h-4" /> : <Play className="w-4 h-4" fill="#000" />} {gameOver ? "Play again" : "Start"}
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="mono text-[9px] uppercase tracking-widest text-white/40 mt-4 text-center flex items-center gap-2 justify-center">
          <Zap className="w-3 h-3" /> canvas 2d · procedural waves · runs entirely in browser
        </div>
      </div>
    </div>
  );
}

function Chip({ label, value, color }) {
  return (
    <div className="hidden md:inline-flex items-center gap-2 mono text-[10px] uppercase tracking-widest px-3 py-1.5 rounded-full border" style={{ borderColor: `${color}55`, background: `${color}10`, color }}>
      <span className="opacity-70">{label}</span>
      <span className="font-bold text-white">{value}</span>
    </div>
  );
}
